/**
 * SignatureBuilder · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §2 — structured signature editor (Name, Title, Department,
 * Company, contacts, logos, profile photo, badges, banner, typography).
 *
 * UX:
 *   • Left pane: structured form, grouped by section
 *   • Right pane: live HTML preview, refreshed on a 300-ms debounce
 *   • Image fields: drag-drop or click to upload; preview thumb;
 *     "Replace" and "Delete" controls per slot
 *
 * Why structured (not WYSIWYG)?
 *   The backend renderer produces bullet-proof inline-CSS table HTML
 *   that survives Outlook 2007-2024, Gmail, Apple Mail, mobile clients.
 *   Free-form rich-text editors mangle Outlook compatibility — this is
 *   why HubSpot / WiseStamp / Newoldstamp all use structured builders.
 *
 * Persistence:
 *   • On open: GET /admin/email/signatures/{id}
 *   • On save: PATCH delta of only changed fields (spec §6)
 *   • Auto-save fires 1 s after the last keystroke (debounced)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi, { ADMIN_API } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  X, Save, Image as ImageIcon, Upload, Trash2, Eye,
  Loader2, AlertCircle, CheckCircle2, Type, Palette, MapPin, Send,
} from "lucide-react";

const TEMPLATES = [
  { id: "classic-left-logo", label: "Classic · Left Logo" },
  { id: "stacked-centered",  label: "Stacked · Centered" },
  { id: "compact-mobile",    label: "Compact · Mobile" },
];

const ACCENT_PRESETS = [
  { id: "navy",   color: "#1e3a8a", label: "Navy" },
  { id: "amber",  color: "#b45309", label: "Amber" },
  { id: "emerald",color: "#047857", label: "Emerald" },
  { id: "slate",  color: "#334155", label: "Slate" },
  { id: "rose",   color: "#9f1239", label: "Rose" },
  { id: "violet", color: "#6d28d9", label: "Violet" },
];

const FONT_PRESETS = [
  "Inter, Arial, sans-serif",
  "Arial, sans-serif",
  "Georgia, 'Times New Roman', serif",
  "Helvetica, Arial, sans-serif",
  "Verdana, Geneva, sans-serif",
];

// ─────────────────────────────────────────────────────────────────────
// Hook · debounced auto-save
// ─────────────────────────────────────────────────────────────────────
// v2.7.45+E13 · 500 ms debounce (was 300 ms) per the editor performance
// fix — every keystroke previously hit the backend, which compiled the
// HTML server-side and pushed it back, causing visible typing lag in
// long signatures. The new flow keeps the local `layout` state as the
// single render source and only roundtrips once typing stops for 500 ms.
// A manual "Save Now" button bypasses the debounce when the user wants
// to flush immediately. Status indicator: pending → saving → saved.
function useDebouncedCallback(fn, delay = 500) {
  const tRef = useRef(null);
  const cancel = useCallback(() => {
    if (tRef.current) { clearTimeout(tRef.current); tRef.current = null; }
  }, []);
  const debounced = useCallback((...args) => {
    cancel();
    tRef.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay, cancel]);
  debounced.cancel = cancel;
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────
export default function SignatureBuilder({ signatureId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [sig, setSig]         = useState(null);
  const [layout, setLayout]   = useState({});
  const [name, setName]       = useState("");
  // v2.7.45+E8 · Test-send picker state
  const [showTestSend, setShowTestSend] = useState(false);
  const [testAccounts, setTestAccounts] = useState([]);
  const [testSendId, setTestSendId]     = useState("");
  const [testSending, setTestSending]   = useState(false);

  // Load the user's mailboxes for the test-send picker. Cached once
  // and reused across opens.
  useEffect(() => {
    let cancelled = false;
    adminApi.get("/admin/email/accounts")
      .then((r) => {
        if (cancelled) return;
        const items = r.data?.items || [];
        setTestAccounts(items);
        const def = items.find((a) => a.is_default) || items[0];
        if (def) setTestSendId(def.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const sendTestEmail = async () => {
    if (!testSendId) { toast.error("Pick a mailbox first"); return; }
    setTestSending(true);
    try {
      await adminApi.post(`/admin/email/signatures/${signatureId}/test-send`, {
        account_id: testSendId,
      });
      const addr = testAccounts.find((a) => a.id === testSendId)?.email || "your inbox";
      toast.success(`Test email sent to ${addr} — check there to see how it renders`);
      setShowTestSend(false);
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTestSending(false);
    }
  };

  // Load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/email/signatures/${signatureId}`);
        if (cancelled) return;
        setSig(data);
        setLayout(data.layout_json || {});
        setName(data.name || "Untitled");
      } catch (e) {
        toast.error(`Could not load signature: ${e?.response?.data?.detail || e.message}`);
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [signatureId, onClose]);

  // v2.7.45+E13 · Save-status indicator state — drives the inline "Saving…
  // / Saved ✓ Xs ago" UI in the editor header. `pending` = a debounced
  // save is queued. `saving` = the API call is in flight. `saved` = the
  // last API call resolved successfully.
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | pending | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const pendingPatchRef = useRef({}); // accumulates layout/name patches between debounce ticks

  // Save (delta PATCH)
  const save = useCallback(async (patch) => {
    // Merge in any patches that arrived while a save was in flight
    const merged = { ...pendingPatchRef.current, ...patch };
    pendingPatchRef.current = {};
    if (!Object.keys(merged).length) {
      setSaveStatus("idle");
      return;
    }
    setSaving(true);
    setSaveStatus("saving");
    try {
      const { data } = await adminApi.patch(`/admin/email/signatures/${signatureId}`, merged);
      setSig(data);
      onSaved?.(data);
      setSaveStatus("saved");
      setLastSavedAt(Date.now());
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  }, [signatureId, onSaved]);

  const debouncedSave = useDebouncedCallback(save, 500);

  // Helper: queue a patch — accumulates into `pendingPatchRef` so multiple
  // updateLayout calls during the debounce window all get included in the
  // single API call that fires.
  const queueSave = useCallback((patch) => {
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    setSaveStatus("pending");
    debouncedSave(patch);
  }, [debouncedSave]);

  // Manual "Save Now" — bypasses the debounce and flushes the queue
  // immediately. Used by the Save Signature button in the editor header.
  const flushSaveNow = useCallback(() => {
    debouncedSave.cancel();
    const queued = { ...pendingPatchRef.current };
    if (!Object.keys(queued).length) {
      // Nothing queued but user might just want to force a save of the
      // current local state — push the full layout + name as a safety net.
      queued.layout_json = layout;
      queued.name = name;
    }
    return save(queued);
  }, [debouncedSave, save, layout, name]);

  const updateLayout = (key, value) => {
    const next = { ...layout, [key]: value };
    setLayout(next);
    queueSave({ layout_json: next });
  };

  const updateTypography = (key, value) => {
    const next = { ...layout, typography: { ...(layout.typography || {}), [key]: value } };
    setLayout(next);
    queueSave({ layout_json: next });
  };

  const renameSig = (v) => {
    setName(v);
    queueSave({ name: v });
  };

  // v2.7.45+E13 · Ctrl+S / Cmd+S keyboard shortcut to flush pending saves
  // immediately. Only active while the editor modal is open.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        flushSaveNow();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flushSaveNow]);

  // Image upload (per-slot)
  const onUpload = async (slotKey, file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large — 100 MB maximum");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", slotKey);
    try {
      const { data } = await adminApi.post(`/admin/email/signatures/${signatureId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Badges are a list, everything else replaces the slot
      if (slotKey === "badge") {
        const newBadges = [...(layout.badges || []), data.id];
        updateLayout("badges", newBadges);
      } else {
        updateLayout(slotKey, data.id);
      }
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onDeleteImage = async (slotKey, imageId) => {
    if (!imageId) return;
    if (!window.confirm("Remove this image from the signature?")) return;
    try {
      await adminApi.delete(`/admin/email/signatures/images/${imageId}`);
      if (slotKey === "badge") {
        updateLayout("badges", (layout.badges || []).filter((b) => b !== imageId));
      } else {
        updateLayout(slotKey, null);
      }
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (loading || !sig) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500"/>
      </div>
    );
  }

  const previewHtml = sig.html || "<div>Preview will appear here</div>";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="signature-builder">
      <div className="bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg w-full max-w-6xl h-[92vh] flex flex-col shadow-2xl">
        <header className="px-5 py-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="min-w-0 flex-1 mr-4">
            <Input
              value={name}
              onChange={(e) => renameSig(e.target.value)}
              className="text-base font-extrabold h-9"
              placeholder="Signature name"
              data-testid="signature-name-input"
            />
            <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-1 flex items-center gap-2" data-testid="signature-save-status">
              {saveStatus === "pending" && (
                <><Loader2 className="h-3 w-3 animate-spin text-amber-500"/>Pending save (typing pause to flush)…</>
              )}
              {saveStatus === "saving" && (
                <><Loader2 className="h-3 w-3 animate-spin text-sky-500"/>Saving…</>
              )}
              {(saveStatus === "saved" || saveStatus === "idle") && (
                <><CheckCircle2 className="h-3 w-3 text-emerald-500"/>
                Saved
                {lastSavedAt
                  ? ` · ${new Date(lastSavedAt).toLocaleTimeString()}`
                  : sig.updated_at && ` · ${new Date(sig.updated_at).toLocaleTimeString()}`}</>
              )}
              {saveStatus === "error" && (
                <><AlertCircle className="h-3 w-3 text-rose-500"/>Save failed — retry from the Save button.</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={flushSaveNow}
              disabled={saveStatus === "saving"}
              className="bg-sky-600 hover:bg-sky-500 text-white h-9 gap-1.5 text-xs"
              data-testid="signature-save-now-btn"
              title="Save Signature now (Ctrl+S)"
            >
              {saveStatus === "saving"
                ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
                : <Save className="h-3.5 w-3.5"/>}
              Save Signature
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} data-testid="signature-builder-close">
              <X className="h-4 w-4"/>
            </Button>
          </div>
        </header>

        {/* Body: 2-column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1 overflow-hidden">
          {/* Editor */}
          <div className="overflow-y-auto p-5 space-y-5 border-r border-slate-200 dark:border-zinc-800">
            {/* Template picker */}
            <Section title="Layout Template" icon={<Type className="h-3 w-3"/>}>
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => updateLayout("template", t.id)}
                    className={`text-xs font-bold px-2 py-2 rounded border transition-colors
                      ${(layout.template || "classic-left-logo") === t.id
                        ? "bg-sky-500 text-white border-sky-500"
                        : "bg-white dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 border-slate-300 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-800"}
                    `}
                    data-testid={`signature-template-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Section>

            {/* Identity */}
            <Section title="Identity">
              <Grid>
                <Field label="Full Name">
                  <Input value={layout.name || ""} onChange={(e) => updateLayout("name", e.target.value)} placeholder="Catherine Osei, CPA, CISA" data-testid="sig-name"/>
                </Field>
                <Field label="Job Title">
                  <Input value={layout.job_title || ""} onChange={(e) => updateLayout("job_title", e.target.value)} placeholder="Senior Manager" data-testid="sig-title"/>
                </Field>
                <Field label="Department">
                  <Input value={layout.department || ""} onChange={(e) => updateLayout("department", e.target.value)} placeholder="Forensic Accounting"/>
                </Field>
                <Field label="Company">
                  <Input value={layout.company || ""} onChange={(e) => updateLayout("company", e.target.value)} placeholder="Buller Taxi Services"/>
                </Field>
              </Grid>
            </Section>

            {/* Contact */}
            <Section title="Contact">
              <Grid>
                <Field label="Email">
                  <Input value={layout.email || ""} onChange={(e) => updateLayout("email", e.target.value)} type="email" placeholder="you@bullertaxi.com"/>
                </Field>
                <Field label="Website">
                  <Input value={layout.website || ""} onChange={(e) => updateLayout("website", e.target.value)} placeholder="bullertaxi.com"/>
                </Field>
                <Field label="Direct phone">
                  <Input value={layout.direct_phone || ""} onChange={(e) => updateLayout("direct_phone", e.target.value)} placeholder="+1 (405) 555-0100"/>
                </Field>
                <Field label="Mobile">
                  <Input value={layout.mobile_phone || ""} onChange={(e) => updateLayout("mobile_phone", e.target.value)} placeholder="+1 (405) 555-0101"/>
                </Field>
                <Field label="Address" full>
                  <Textarea value={layout.address || ""} onChange={(e) => updateLayout("address", e.target.value)} rows={2} placeholder="180 N LaSalle St., Suite 2200, Chicago, IL 60601"/>
                </Field>
              </Grid>
            </Section>

            {/* Images */}
            <Section title="Images" icon={<ImageIcon className="h-3 w-3"/>}>
              <div className="grid grid-cols-2 gap-3">
                <ImageSlot label="Company Logo"  imageId={layout.company_logo}  onUpload={(f) => onUpload("company_logo", f)}  onDelete={() => onDeleteImage("company_logo", layout.company_logo)} testid="company_logo"/>
                <ImageSlot label="Profile Photo" imageId={layout.profile_photo} onUpload={(f) => onUpload("profile_photo", f)} onDelete={() => onDeleteImage("profile_photo", layout.profile_photo)} testid="profile_photo"/>
                <ImageSlot label="Brand Logo"    imageId={layout.brand_logo}    onUpload={(f) => onUpload("brand_logo", f)}    onDelete={() => onDeleteImage("brand_logo", layout.brand_logo)} testid="brand_logo"/>
                <ImageSlot label="Partner Logo"  imageId={layout.partner_logo}  onUpload={(f) => onUpload("partner_logo", f)}  onDelete={() => onDeleteImage("partner_logo", layout.partner_logo)} testid="partner_logo"/>
                <ImageSlot label="Banner"        imageId={layout.banner}        onUpload={(f) => onUpload("banner", f)}        onDelete={() => onDeleteImage("banner", layout.banner)} testid="banner"/>
                <BadgeStrip
                  badges={layout.badges || []}
                  onAdd={(f) => onUpload("badge", f)}
                  onRemove={(id) => onDeleteImage("badge", id)}
                />
              </div>
            </Section>

            {/* Typography */}
            <Section title="Typography" icon={<Palette className="h-3 w-3"/>}>
              <Grid>
                <Field label="Accent color">
                  <div className="flex gap-1 flex-wrap">
                    {ACCENT_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        title={p.label}
                        className={`w-7 h-7 rounded border-2 transition-transform ${(layout.typography?.accent_color || "#1e3a8a") === p.color ? "scale-110 border-slate-900 dark:border-white" : "border-transparent"}`}
                        style={{ backgroundColor: p.color }}
                        onClick={() => updateTypography("accent_color", p.color)}
                        data-testid={`sig-accent-${p.id}`}
                      />
                    ))}
                    <Input
                      type="text"
                      value={layout.typography?.accent_color || ""}
                      onChange={(e) => updateTypography("accent_color", e.target.value)}
                      placeholder="#1e3a8a"
                      className="w-24 h-7 text-xs font-mono"
                    />
                  </div>
                </Field>
                <Field label="Font family">
                  <select
                    value={layout.typography?.font_family || FONT_PRESETS[0]}
                    onChange={(e) => updateTypography("font_family", e.target.value)}
                    className="h-9 w-full rounded border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-xs"
                  >
                    {FONT_PRESETS.map((f) => (<option key={f} value={f}>{f}</option>))}
                  </select>
                </Field>
              </Grid>
            </Section>
          </div>

          {/* Live preview */}
          <div className="overflow-y-auto p-5 bg-slate-50 dark:bg-zinc-950/80">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-3 flex items-center gap-1.5">
              <Eye className="h-3 w-3"/>Live Preview · Compiled HTML
            </div>
            <div
              className="bg-white p-6 rounded shadow-sm border border-slate-200"
              data-testid="signature-preview"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
            <div className="mt-3 text-[10px] text-slate-500 dark:text-zinc-500">
              Disclaimers are auto-appended by the rendering engine. To edit them, an admin must use Mail Account Settings → Global Disclaimer.
            </div>
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] text-slate-500 dark:text-zinc-500">
            Compiled HTML auto-saves 300 ms after each edit · safe for Outlook, Gmail, Apple Mail
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* v2.7.45+E8 · Inline test-send picker (collapsed by default
                so the footer stays clean; expands into a select + Send
                button when the user clicks Send Test). */}
            {showTestSend ? (
              <>
                <select
                  value={testSendId}
                  onChange={(e) => setTestSendId(e.target.value)}
                  disabled={testSending}
                  className="h-8 rounded border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-2 text-xs"
                  data-testid="signature-test-mailbox"
                >
                  {testAccounts.length === 0 && <option value="">(no mailboxes)</option>}
                  {testAccounts.map((a) => (
                    <option key={a.id} value={a.id}>From: {a.email}</option>
                  ))}
                </select>
                <Button
                  onClick={sendTestEmail}
                  disabled={testSending || !testSendId}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-8 text-xs gap-1.5"
                  data-testid="signature-test-send-confirm"
                >
                  {testSending ? <Loader2 className="h-3 w-3 animate-spin"/> : <Send className="h-3 w-3"/>}
                  Send
                </Button>
                <Button
                  variant="ghost" size="sm" className="h-8 text-xs"
                  onClick={() => setShowTestSend(false)}
                  disabled={testSending}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowTestSend(true)}
                className="h-8 text-xs gap-1.5"
                data-testid="signature-test-send-btn"
                title="Email this signature to yourself to preview client rendering"
              >
                <Send className="h-3 w-3"/>Send test email
              </Button>
            )}
            <Button onClick={onClose} className="bg-sky-600 hover:bg-sky-500 text-white font-bold gap-2" data-testid="signature-builder-done">
              <CheckCircle2 className="h-4 w-4"/>Done
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-2 flex items-center gap-1.5">
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, full = false, children }) {
  return (
    <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}

function ImageSlot({ label, imageId, onUpload, onDelete, testid }) {
  const fileRef = useRef(null);
  return (
    <div className="border border-dashed border-slate-300 dark:border-zinc-700 rounded p-2 bg-white dark:bg-zinc-900/40">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-1.5">{label}</div>
      {imageId ? (
        <div className="space-y-2">
          <img
            src={`${ADMIN_API}/signature-images/${imageId}`}
            alt={label}
            loading="lazy"
            className="w-full max-h-24 object-contain bg-slate-100 dark:bg-zinc-800 rounded"
            data-testid={`sig-img-${testid}`}
          />
          <div className="flex gap-1">
            <Button
              size="sm" variant="outline" className="h-6 px-2 text-[10px] flex-1 gap-1"
              onClick={() => fileRef.current?.click()}
              data-testid={`sig-img-replace-${testid}`}
            >
              <Upload className="h-3 w-3"/>Replace
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-6 px-2 text-[10px] text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20"
              onClick={onDelete}
              data-testid={`sig-img-delete-${testid}`}
            >
              <Trash2 className="h-3 w-3"/>
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full h-20 flex flex-col items-center justify-center gap-1 text-[11px] text-slate-500 dark:text-zinc-500 hover:bg-slate-50 dark:hover:bg-zinc-800/40 rounded transition-colors"
          data-testid={`sig-img-upload-${testid}`}
        >
          <Upload className="h-4 w-4"/>
          Click to upload
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function BadgeStrip({ badges, onAdd, onRemove }) {
  const fileRef = useRef(null);
  return (
    <div className="border border-dashed border-slate-300 dark:border-zinc-700 rounded p-2 bg-white dark:bg-zinc-900/40 col-span-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 mb-1.5">
        Badges &amp; Certifications ({badges.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {badges.map((id) => (
          <div key={id} className="relative group">
            <img
              src={`${ADMIN_API}/signature-images/${id}`}
              alt="badge"
              loading="lazy"
              className="w-12 h-12 object-contain bg-slate-100 dark:bg-zinc-800 rounded border border-slate-200 dark:border-zinc-700"
            />
            <button
              type="button"
              onClick={() => onRemove(id)}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title="Remove badge"
              data-testid={`sig-badge-remove-${id}`}
            >
              <X className="h-3 w-3"/>
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-12 h-12 flex items-center justify-center text-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800/40 rounded border border-dashed border-slate-300 dark:border-zinc-700"
          title="Add badge"
          data-testid="sig-badge-add"
        >
          <Upload className="h-3.5 w-3.5"/>
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onAdd(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
